import { forwardRef } from 'react';
import type { InputHTMLAttributes } from 'react';
import { clsx } from 'clsx';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ 
    label, 
    error, 
    helperText, 
    fullWidth = false,
    className,
    id,
    ...props 
  }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    
    const baseClasses = 'px-4 py-2 rounded border bg-dark-700 text-white placeholder-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark-900';
    
    const stateClasses = error 
      ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
      : 'border-dark-600 focus:border-primary-500 focus:ring-primary-500';
    
    const widthClasses = fullWidth ? 'w-full' : '';
    
    return (
      <div className={fullWidth ? 'w-full' : ''}>
        {label && (
          <label 
            htmlFor={inputId} 
            className="block text-sm font-medium text-gray-300 mb-2"
          >
            {label}
          </label>
        )}
        
        <input
          ref={ref}
          id={inputId}
          className={clsx(
            baseClasses,
            stateClasses,
            widthClasses,
            className
          )}
          {...props}
        />
        
        {(error || helperText) && (
          <div className="mt-1 text-sm">
            {error ? (
              <span className="text-red-500">{error}</span>
            ) : (
              <span className="text-gray-400">{helperText}</span>
            )}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';